import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Phone, 
  Plus, 
  RefreshCw, 
  User, 
  CheckCircle, 
  XCircle,
  ArrowLeft,
  Trash2,
  Mail
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNumbers = () => {
  const [numbers, setNumbers] = useState([]);
  const [stats, setStats] = useState({ total: 0, available: 0, assigned: 0 });
  const [loading, setLoading] = useState(true);
  const [newNumber, setNewNumber] = useState("");
  const [assignEmail, setAssignEmail] = useState("");
  const [selectedNumber, setSelectedNumber] = useState(null);

  const fetchNumbers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/numbers/pool`);
      const data = await response.json();
      setNumbers(data.numbers || []);
      setStats({
        total: data.total || 0,
        available: data.available || 0,
        assigned: data.assigned || 0
      });
    } catch (error) {
      toast.error("Erreur lors du chargement des numéros");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNumbers();
  }, []);

  const handleAddNumber = async (e) => {
    e.preventDefault();
    if (!newNumber.trim()) {
      toast.error("Veuillez entrer un numéro");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/numbers/pool/add?number=${encodeURIComponent(newNumber)}&provider=ovh`, {
        method: "POST"
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Numéro ${newNumber} ajouté au pool`);
        setNewNumber("");
        fetchNumbers();
      } else {
        toast.error(data.detail || "Erreur lors de l'ajout");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout du numéro");
      console.error(error);
    }
  };

  const handleAssignNumber = async (e) => {
    e.preventDefault();
    if (!assignEmail.trim()) {
      toast.error("Veuillez entrer un email");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/numbers/assign?user_email=${encodeURIComponent(assignEmail)}`, {
        method: "POST"
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Numéro attribué à ${assignEmail}`);
        setAssignEmail("");
        fetchNumbers();
      } else {
        toast.error(data.detail || "Erreur lors de l'attribution");
      }
    } catch (error) {
      toast.error("Erreur lors de l'attribution");
      console.error(error);
    }
  };

  const handleReleaseNumber = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/numbers/release?user_email=${encodeURIComponent(email)}`, {
        method: "POST"
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success("Numéro libéré");
        fetchNumbers();
      } else {
        toast.error(data.detail || "Erreur lors de la libération");
      }
    } catch (error) {
      toast.error("Erreur lors de la libération");
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/app/messages" className="flex items-center text-gray-500 hover:text-gray-700">
                <ArrowLeft className="h-5 w-5 mr-2" />
                Retour
              </Link>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🍓</span>
                <span className="text-xl font-bold">Fruitycall Admin</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchNumbers}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total numéros</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Disponibles</p>
                  <p className="text-3xl font-bold text-green-600">{stats.available}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Attribués</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.assigned}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Actions Panel */}
          <div className="space-y-6">
            {/* Add Number Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Plus className="h-5 w-5 mr-2 text-green-600" />
                  Ajouter un numéro
                </CardTitle>
                <CardDescription>
                  Ajoutez un numéro OVH au pool
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddNumber} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newNumber">Numéro de téléphone</Label>
                    <Input
                      id="newNumber"
                      placeholder="01 84 24 XX XX"
                      value={newNumber}
                      onChange={(e) => setNewNumber(e.target.value)}
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter au pool
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Assign Number Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-600" />
                  Attribuer un numéro
                </CardTitle>
                <CardDescription>
                  Attribuez un numéro disponible à un utilisateur
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAssignNumber} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="assignEmail">Email de l'utilisateur</Label>
                    <Input
                      id="assignEmail"
                      type="email"
                      placeholder="utilisateur@exemple.fr"
                      value={assignEmail}
                      onChange={(e) => setAssignEmail(e.target.value)}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={stats.available === 0}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Attribuer
                  </Button>
                  {stats.available === 0 && (
                    <p className="text-sm text-orange-600 text-center">
                      Aucun numéro disponible
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Numbers List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pool de numéros</CardTitle>
                <CardDescription>
                  Gérez tous les numéros Fruitycall
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : numbers.length === 0 ? (
                  <div className="text-center py-8">
                    <Phone className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Aucun numéro dans le pool</p>
                    <p className="text-sm text-gray-400">
                      Ajoutez votre premier numéro OVH
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {numbers.map((num, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          num.status === "available"
                            ? "border-green-200 bg-green-50"
                            : "border-orange-200 bg-orange-50"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              num.status === "available" ? "bg-green-200" : "bg-orange-200"
                            }`}>
                              <Phone className={`h-5 w-5 ${
                                num.status === "available" ? "text-green-700" : "text-orange-700"
                              }`} />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {num.number}
                              </p>
                              <p className="text-sm text-gray-500">
                                {num.provider?.toUpperCase() || "OVH"}
                                {num.is_main_number && (
                                  <span className="ml-2 text-xs text-blue-600">(Principal)</span>
                                )}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <Badge variant={num.status === "available" ? "success" : "warning"}>
                              {num.status === "available" ? "Disponible" : "Attribué"}
                            </Badge>
                            
                            {num.status === "assigned" && num.assigned_to && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleReleaseNumber(num.assigned_to)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {num.status === "assigned" && num.assigned_to && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="h-4 w-4 mr-2" />
                              {num.assigned_to}
                              <span className="ml-4 text-gray-400">
                                Depuis : {formatDate(num.assigned_at)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminNumbers;
